import { useStore } from 'vuex'
import { computed, ref, watchEffect, watch, onUnmounted } from 'vue'
import { initialPagination } from 'src/vuex/modules/bundle-submission'
import { cloneDeep, each, head, isEmpty, isEqual, map, uniqBy, filter } from 'lodash'
import Close from 'components/icons/close/Close.vue'
import SearchIcon from '../icons/search-icon/index.vue'
import AnimationProcessing from 'components/animations/processing'
import FamilyItem from 'src/pages/bundle-submission/components/family-item/FamilyItem.vue'
import ArrowBack from '../icons/arrow-back/ArrowBack.vue'
import { disableScrollOnModalOpen } from 'src/utils/helper/misc'

export default {
  props: {
    isAddFontsSideBarOpen: {
      type: Boolean,
      required: false
    }
  },
  components: {
    Close,
    SearchIcon,
    AnimationProcessing,
    FamilyItem,
    ArrowBack
  },
  setup(props, { emit }) {
    const store = useStore()

    /* feature infinite scroll starts */
    const families = computed({
      get: () => store.state.bundle.families,
      set: familiesData => store.commit('bundle/setFamilies', familiesData)
    })

    const formattedFamilies = computed(() => store.getters['bundle/formattedFamilies'])

    const pagination = computed({
      get() {
        return store.state.bundle.pagination
      },
      set(paginationData) {
        store.commit('bundle/setPagination', paginationData)
      }
    })

    const bundleInfo = computed({
      get: () => store.state.bundle.bundleInfo,
      set: val => store.commit('bundle/UPDATE_BUNDLE_INFO', val)
    })

    /* feature search families starts */
    const familyLoadedDone = ref(false)

    const searchTerm = ref('')

    const responseSearchTerm = ref('')

    const isValidSearch = computed(() => searchTerm.value.trim().length >= 1)

    async function handleArrowBack() {
      pagination.value = cloneDeep(initialPagination)
      families.value = []
      searchTerm.value = ''
      responseSearchTerm.value = ''
      familyLoadedDone.value = false

      await loadMore()
    }

    const searchInput = ref(null)

    async function searchFamilies() {
      // if its already loading, don't do anything
      if (isFamiliesLoading.value) {
        searchTerm.value = ''
        return
      }

      if (isEmpty(searchTerm.value)) return

      if (!isValidSearch.value) return

      families.value = []
      pagination.value = cloneDeep(initialPagination)
      familyLoadedDone.value = false

      await loadMore()

      responseSearchTerm.value = searchTerm.value
      searchInput.value.focus()
    }

    async function handleSearchInput() {
      if (isEmpty(searchTerm.value.trim()) && !isEmpty(responseSearchTerm.value.trim())) {
        clearSearchTerm()
      }
    }

    async function clearSearchTerm() {
      searchTerm.value = ''
      responseSearchTerm.value = ''

      isFamiliesLoading.value = false

      families.value = []
      pagination.value = cloneDeep(initialPagination)
      familyLoadedDone.value = false

      await loadMore()
      searchInput.value?.focus()
    }

    /* feature search families ends */

    function fetchFamiliesByFoundries() {
      return store.dispatch('bundle/fetchFamiliesByFoundries', {
        foundryIds: bundleInfo.value.foundries.map(foundry => foundry.value || foundry.id).join(','),
        page: pagination.value.page,
        perPage: pagination.value.perPage,
        search: searchTerm.value,
        includeCount: true
      })
    }

    const isFamiliesLoading = computed({
      get: () => store.state.bundle.isFamiliesLoading,
      set: val => store.commit('bundle/setFamiliesLoading', val)
    })

    async function loadMore() {
      if (familyLoadedDone.value) return

      /* Since, families loading api is hit when foundries select is blurred, we cannot load the families again as isFamiliesLoading is true so, after isFamiliesLoading is false it can continue fetching */
      if (isFamiliesLoading.value) return

      if (!props.isAddFontsSideBarOpen) return

      isFamiliesLoading.value = true
      try {
        const res = await fetchFamiliesByFoundries()

        const { meta, families: responseFamilies } = res.data

        if (isEmpty(responseFamilies)) {
          isFamiliesLoading.value = false
          pagination.value = {
            ...pagination.value,
            ...meta
          }
          familyLoadedDone.value = true
          return
        }

        families.value = [...families.value, ...responseFamilies]
        isFamiliesLoading.value = false

        if (isEqual(meta.nextPage, pagination.value.page)) {
          return
        }

        pagination.value = {
          ...pagination.value,
          ...meta
        }

        pagination.value.page++
        pagination.value.nextPage = meta?.nextPage
      } catch (error) {
        isFamiliesLoading.value = false
        families.value = []
        pagination.value = cloneDeep(initialPagination)
      }
    }

    const scrollElement = ref(null)

    watchEffect(() => {
      if (scrollElement.value) {
        scrollElement.value.addEventListener('scroll', checkForInfiniteScroll)
      }
    })

    async function checkForInfiniteScroll() {
      const scrollableHeight = scrollElement.value.scrollHeight - scrollElement.value.clientHeight

      if (Math.round(scrollElement.value.scrollTop) >= scrollableHeight) {
        if (pagination.value.nextPage < pagination.value.page) return
        await loadMore()
      }
    }
    /* feature infinite scroll ends */

    /* feature get family details starts */
    const STYLE_FORMAT = 'OT-CFF'
    /**
     *
     * @param {String} familyId
     */
    async function getFamilyDetail(familyId) {
      if (isEmpty(familyId)) return

      const family = formattedFamilies.value.find(familyData => familyData.familyId === familyId)

      if (isEmpty(family)) return

      if (!isEmpty(family?.skus)) return

      family.isLoading = true

      const response = await store.dispatch('promotions/getTaggedFamilies', {
        familyIds: [familyId]
      })

      const data = head(response)

      if (isEmpty(data)) {
        family.isLoading = false
        return
      }

      if (data.id) {
        /* remove duplicate styles and only consider OT-CFF format. This will remove duplicate styles from its own styles array
        * e.g sku.styles = [ { format: 'OT-CFF', styleId: '1'}, { format: 'OT-TFF', styleId: '1'}, ]
        * The below loop wil filter out OT-TFF format styles.
        */
        each(data.skus, sku => {
          const styleIds = map(sku.styles, 'styleId')
          if (styleIds.length >= 2) {
            const styles = filter(sku.styles, style => isEqual(style.format, STYLE_FORMAT))
            sku.styles = uniqBy(styles, 'styleId')
          }
        })

        family.skus = data.skus
        family.isLoading = false
      }
    }

    /* feature get family details ends */

    /**
     * Toggle add fonts tick icon
     * @param {Object} payload
     */
    const toggleAddFont = payload => {
      store.commit('bundle/UPDATE_FONT_LIST', payload)
      emit('update:bundle')
    }

    // Function to close the sidebar and restore scrolling
    const closeSidebar = () => {
      emit('close')
      disableScrollOnModalOpen(false)
    }

    // Watch for sidebar open/close changes to toggle body scroll
    watch(() => props.isAddFontsSideBarOpen, (newValue) => {
      disableScrollOnModalOpen(newValue)
    }, { immediate: true })

    // Make sure to enable scrolling when component is unmounted
    onUnmounted(() => {
      disableScrollOnModalOpen(false)
    })

    return {
      searchFamilies,
      searchInput,
      searchTerm,
      isFamiliesLoading,
      handleSearchInput,
      responseSearchTerm,
      handleArrowBack,
      pagination,
      isEmpty,
      formattedFamilies,
      getFamilyDetail,
      toggleAddFont,
      families,
      loadMore,
      fetchFamiliesByFoundries,
      isValidSearch,
      clearSearchTerm,
      familyLoadedDone,
      bundleInfo,
      scrollElement,
      closeSidebar
    }
  }
}
